import { MouseEvent, useMemo, useState } from 'react';
import { Avatar, Box, Modal } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCopy,
  faEnvelope,
  faReply,
  faXmark,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import toast from 'react-hot-toast';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';

type UserEmailModalProps = {
  open: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  profilePicture?: string;
  email?: string | null;
  marketing?: boolean | null;
  onReplyInDM?: () => void;
};

const PRIMARY_PURPLE = 'var(--sys-primary-P-80, #5F1ECC)'; // matches Add comment
const SUCCESS_COLOR = '#16a34a';
const ERROR_COLOR = '#dc2626';

export const UserEmailModal = ({
  open,
  onClose,
  userId,
  username,
  profilePicture,
  email,
  marketing,
  onReplyInDM,
}: UserEmailModalProps) => {
  const [copied, setCopied] = useState(false);
  const marketingLabel = useMemo(() => {
    if (marketing === true) return 'Yes';
    if (marketing === false) return 'No';
    return null;
  }, [marketing]);
  const displayEmail =
    marketing === false || !email ? '************' : email || '************';

  const handleClose = (event: { stopPropagation?: () => void }) => {
    event.stopPropagation?.();
    onClose();
    setCopied(false);
  };

  const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopied(true);
  };

  const marketingColor =
    marketingLabel === 'Yes'
      ? SUCCESS_COLOR
      : marketingLabel === 'No'
      ? ERROR_COLOR
      : 'text.primary';

  const isCopyDisabled = !email || marketing === false;
  const copyColor = copied
    ? SUCCESS_COLOR
    : isCopyDisabled
    ? '#9ca3af'
    : PRIMARY_PURPLE;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 4,
        }}
        onClick={handleClose}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: 4,
            p: 3,
            width: '460px',
            maxWidth: '100%',
          }}
          onClick={(event) => event.stopPropagation()}
          data-testid="userEmailModal"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography style={{ fontWeight: 700, fontSize: '20px' }}>
              User Profile
            </Typography>
            <button
              aria-label="Close"
              style={{
                padding: 4,
                color: '#8b8b8b',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
              onClick={handleClose}
              data-testid="userEmailModalClose"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar alt={username} src={profilePicture || ''} />
              <Box>
                <Typography style={{ fontWeight: 600 }}>{username}</Typography>
                <Typography style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  #{userId}
                </Typography>
              </Box>
            </Box>
            {onReplyInDM && (
              <Button
                variant="text"
                leftIcon={faReply}
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                  onReplyInDM();
                }}
                style={{
                  color: PRIMARY_PURPLE,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
                data-testid="userEmailModalReplyInDM"
              >
                Reply via DM
              </Button>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid #ECEDF2', pt: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                rowGap: 2,
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FontAwesomeIcon icon={faEnvelope} color="#131313" width={14} />
                <Typography
                  style={{
                    fontSize: '0.875rem',
                    color: displayEmail ? '#111827' : '#6b7280',
                  }}
                  data-testid="userEmailModalEmail"
                >
                  {displayEmail}
                </Typography>
              </Box>
              <Button
                variant="text"
                leftIcon={copied ? faCircleCheck : faCopy}
                onClick={handleCopy}
                disabled={isCopyDisabled}
                style={{
                  color: copyColor,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  textAlign: 'center',
                }}
                data-testid="userEmailModalCopy"
              >
                {copied ? (
                  <span
                    className="flex items-center gap-1"
                    style={{ color: SUCCESS_COLOR }}
                  >
                    Copied
                  </span>
                ) : (
                  'Copy'
                )}
              </Button>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} />
              {marketingLabel !== null && (
                <Typography
                  style={{
                    gridColumn: '2 / 3',
                    fontSize: '0.875rem',
                    color: '#111827',
                    textAlign: 'right',
                    fontWeight: 400,
                  }}
                >
                  Marketing:{' '}
                  <span style={{ color: marketingColor }}>
                    {marketingLabel}
                  </span>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
